import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onboardingApi } from "../../services/onboardingApi";
import { employeeApi } from "../../services/employeeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { useLocalization } from "../../hooks/useLocalization";

export default function OnboardingQueuePage() {
  const { t } = useLocalization();
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    Promise.all([onboardingApi.listCases(), employeeApi.list()]).then(([c, e]) => {
      setCases(c.data);
      const map = {};
      e.data.forEach((emp) => { map[emp.id] = emp; });
      setEmployees(map);
      setLoading(false);
    }).catch((err) => {
      if (err.response?.status === 403) setAccessDenied(true);
      setLoading(false);
    });
  }, []);

  if (accessDenied) return <AccessDenied resourceLabel={t("nav.onboarding")} />;

  return (
    <div data-testid="onboarding-queue-page">
      <PageHelpBanner pageKey="onboarding-queue" title="How to use this page">
        <p>Every employee currently going through onboarding is listed here. Click a name to open their onboarding case - review each checklist requirement, upload/approve documents, and clear them to work once every blocking requirement is complete.</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("nav.onboarding")}</h2>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("employees.employeeId")}</th>
              <th className="px-4 py-3 font-medium">{t("employees.legalName")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {!loading && cases.map((c) => {
              const emp = employees[c.employee_id];
              return (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">{emp?.employee_id || "-"}</td>
                  <td className="px-4 py-3">
                    <Link to={`/management/onboarding/${c.id}`} data-testid={`onboarding-case-row-${c.id}`} className="font-medium text-blue-600 hover:underline">
                      {emp?.legal_name || "Unknown"}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && cases.length === 0 && <EagleState variant="empty" title={t("eagle.noOnboardingCasesTitle")} message={t("eagle.noOnboardingCasesMessage")} testId="onboarding-queue-empty" />}
      </div>
    </div>
  );
}
